"use client";

import ErrorBoundary from "@/components/common/ErrorBoundary";
import EsimDestinationCard from "@/components/esim/EsimDestinationCard";
import EsimDiscoverByGroup from "@/components/esim/EsimDiscoverByGroup";
import EsimFeaturedCollections from "@/components/esim/EsimFeaturedCollections";
import EsimProductHomeContent from "@/components/esim/EsimProductHomeContent";
import { ESIM_POPULAR_COUNTRY_CODES } from "@/constants/esim/esimPopularCountries";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useEsimCountriesData } from "@/hooks/esim/useEsimCountriesData";
import { useEsimGoodPriceCountry } from "@/hooks/esim/useEsimGoodPriceCountry";
import { EsimContinent, EsimCountry } from "@/lib/redux/features/esim/types";
import { filterEsimCountries } from "@/utils/esim/filterEsimCountries";
import { getFormatCurrency } from "@/utils/string/formatCurrency";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Skeleton, Spin } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type TabKey = "general" | "popular" | "country" | "region";

/** Số card mỗi "đợt" — dùng cho cả nút "Xem thêm" (duyệt mặc định) lẫn mỗi đợt tự lộ khi search. */
const COUNTRY_PAGE_SIZE = 16;
/** Gõ dưới ngưỡng này thì coi như chưa search gì (không lọc/lộ thêm card) — tránh 1 ký tự phổ biến
 * (vd "a") lộ ra cả trăm card cùng lúc, và giảm số lần tính lọc khi gõ nhanh. */
const MIN_SEARCH_LENGTH = 2;
/** Debounce trước khi thực sự lọc/lộ thêm card theo từ khoá — gõ nhanh không tính toán lại liên tục. */
const SEARCH_DEBOUNCE_MS = 300;
/** Độ trễ giữa mỗi đợt tự lộ card khi search — search ra nhiều kết quả thì lộ dần từng đợt
 * `COUNTRY_PAGE_SIZE` thay vì render hết 1 lần, tránh giật khi search ra hàng trăm kết quả. */
const REVEAL_BATCH_DELAY_MS = 220;

/** Tab "Chung/Phổ biến/Quốc gia/Khu Vực" — đặt giữa banner khuyến mãi và "Danh mục eSIM nổi bật"
 * (`EsimHubPageClient`). Tab "Chung" giữ nguyên nội dung cũ; 3 tab còn lại duyệt eSIM bằng
 * card ảnh (`EsimDestinationCard`) thay cho thanh search cũ (xem docs/planBuild/plan_improve.md). */
const EsimHubTabs: FC = () => {
  const { t } = useTranslation();
  const router = useTERouter();
  const [tab, setTab] = useState<TabKey>("general");
  // Giá trị gõ tức thời (ô input bám theo cái này, gõ mượt) — filter/lộ card thật sự dùng
  // `debouncedQuery` bên dưới (có độ trễ + ngưỡng ký tự tối thiểu).
  const [countryQuery, setCountryQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // Mã quốc gia đã từng render ít nhất 1 lần — CHỈ TĂNG, không bao giờ bớt/reset. Search xong xoá đi
  // rồi search lại, hay bấm "Xem thêm" nhiều lần, card nào đã render 1 lần thì không mount lại nữa
  // (giữ nguyên cache ảnh lỗi + tránh re-render hàng loạt — xem đánh giá trong hội thoại).
  const [renderedCountryCodes, setRenderedCountryCodes] = useState<Set<string>>(new Set());

  // Dưới `MIN_SEARCH_LENGTH` ký tự thì coi như chưa search (xoá debouncedQuery ngay, không cần đợi) —
  // đủ ký tự thì đợi `SEARCH_DEBOUNCE_MS` sau khi ngừng gõ mới thực sự lọc/lộ thêm card.
  useEffect(() => {
    const trimmed = countryQuery.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [countryQuery]);

  const { countries, continents: rawContinents, loading: countriesLoading } = useEsimCountriesData();
  const { goodPriceByCountry } = useEsimGoodPriceCountry();

  // router.push() (useTERouter) là object/hàm mới mỗi lần render — giữ ref tới bản mới nhất để các
  // handler cache bên dưới có thể ổn định vĩnh viễn (không đổi reference) mà vẫn gọi đúng logic mới nhất.
  const routerRef = useRef(router);
  routerRef.current = router;

  // Cache 1 hàm onClick CỐ ĐỊNH cho mỗi mã quốc gia — bắt buộc để `React.memo` trên EsimDestinationCard
  // có tác dụng (nếu tạo hàm mới mỗi lần `.map()` chạy, memo sẽ luôn thấy prop onClick "đổi" và vẫn
  // render lại toàn bộ dù các prop khác không đổi).
  const goToCountryHandlersRef = useRef(new Map<string, () => void>());
  const getGoToCountryHandler = (code: string) => {
    const cache = goToCountryHandlersRef.current;
    let handler = cache.get(code);
    if (!handler) {
      handler = () => routerRef.current.push(`/esim-du-lich/ket-qua-tim-kiem?countries=${code}`);
      cache.set(code, handler);
    }
    return handler;
  };

  const goToRegionHandlersRef = useRef(new Map<string, () => void>());
  const getGoToRegionHandler = (continent: EsimContinent) => {
    const cache = goToRegionHandlersRef.current;
    const key = continent.continent_code || String(continent.id);
    let handler = cache.get(key);
    if (!handler) {
      handler = () => {
        const codes = countries
          .filter((country) => country.continent === continent.continent_name.vi)
          .map((country) => country.code);
        if (!codes.length) return;
        routerRef.current.push(
          `/esim-du-lich/ket-qua-tim-kiem?countriesAny=${codes.join(",")}&regionName=${encodeURIComponent(continent.continent_name.vi)}`,
        );
      };
      cache.set(key, handler);
    }
    return handler;
  };

  // `continents` lấy thẳng từ payload API (`extra.continent_country`), không qua bước sanitize như
  // `countries` — lọc bỏ trước mục nào thiếu field bắt buộc (`continent_name.vi`) để tránh crash khi
  // render/click (`continent.continent_name.vi`), thay vì chỉ trông chờ ErrorBoundary bắt lỗi sau đó.
  const continents = useMemo(
    () => rawContinents.filter((continent) => !!continent?.continent_name?.vi),
    [rawContinents],
  );

  const countryByCode = useMemo(() => {
    const map = new Map<string, EsimCountry>();
    countries.forEach((country) => map.set(country.code, country));
    return map;
  }, [countries]);

  // Tab "Quốc gia" ưu tiên hiện 16 nước "Phổ biến" lên đầu (đúng thứ tự trong
  // `ESIM_POPULAR_COUNTRY_CODES`), phần còn lại giữ nguyên thứ tự trả về từ API.
  const countriesForBrowsing = useMemo(() => {
    const popularSet = new Set<string>(ESIM_POPULAR_COUNTRY_CODES);
    const popular = ESIM_POPULAR_COUNTRY_CODES.map((code) => countryByCode.get(code)).filter(
      (country): country is EsimCountry => !!country,
    );
    const rest = countries.filter((country) => !popularSet.has(country.code));
    return [...popular, ...rest];
  }, [countries, countryByCode]);

  const getDescription = (code: string) => {
    const entry = goodPriceByCountry?.[code];
    if (!entry) return t("esim.destination_default_desc");
    const finalPrice = entry.price + (entry.feeMakeUp ?? 0);
    return `${t("esim.from_price")} ${getFormatCurrency(finalPrice)}`;
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: "general", label: t("esim.hub_tab_general") },
    { key: "popular", label: t("esim.hub_tab_popular") },
    { key: "country", label: t("esim.hub_tab_country") },
    { key: "region", label: t("esim.hub_tab_region") },
  ];

  // Seed "Xem thêm" ban đầu — ngay khi countries tải xong, render trước 1 trang mặc định.
  useEffect(() => {
    if (!countries.length || renderedCountryCodes.size) return;
    setRenderedCountryCodes(
      new Set(countriesForBrowsing.slice(0, COUNTRY_PAGE_SIZE).map((country) => country.code)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  const matchingCountryCodes = useMemo(
    () => new Set(filterEsimCountries(countries, debouncedQuery).map((country) => country.code)),
    [countries, debouncedQuery],
  );

  // Đang search (đủ ký tự + qua debounce) thì tự lộ card khớp theo TỪNG ĐỢT (COUNTRY_PAGE_SIZE/đợt,
  // cách nhau REVEAL_BATCH_DELAY_MS) thay vì lộ hết 1 lần — search ra nhiều kết quả thì hiện dần dần,
  // tránh giật. Card nào đã lộ rồi thì giữ nguyên (không bớt lại khi xoá từ khoá — "đã render thì
  // không render lại").
  const [isRevealingBatch, setIsRevealingBatch] = useState(false);

  useEffect(() => {
    if (!debouncedQuery) {
      setIsRevealingBatch(false);
      return;
    }
    const pending: string[] = [];
    matchingCountryCodes.forEach((code) => {
      if (!renderedCountryCodes.has(code)) pending.push(code);
    });
    if (!pending.length) {
      setIsRevealingBatch(false);
      return;
    }

    let cancelled = false;
    let index = 0;
    let timer: ReturnType<typeof setTimeout>;
    setIsRevealingBatch(true);

    const revealNextBatch = () => {
      if (cancelled) return;
      const batch = pending.slice(index, index + COUNTRY_PAGE_SIZE);
      setRenderedCountryCodes((prev) => {
        const next = new Set(prev);
        batch.forEach((code) => next.add(code));
        return next;
      });
      index += COUNTRY_PAGE_SIZE;
      if (index < pending.length) {
        timer = setTimeout(revealNextBatch, REVEAL_BATCH_DELAY_MS);
      } else {
        setIsRevealingBatch(false);
      }
    };

    revealNextBatch();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, matchingCountryCodes]);

  // Số card khớp search nhưng CHƯA lộ — hiện tối đa 1 đợt skeleton (COUNTRY_PAGE_SIZE) để báo còn
  // đang tải dần, không hiện skeleton cho toàn bộ số còn lại (tránh lặp lại đúng vấn đề "quá nhiều
  // node cùng lúc" đang cố tránh).
  const pendingCountryCount = useMemo(() => {
    if (!debouncedQuery) return 0;
    let count = 0;
    matchingCountryCodes.forEach((code) => {
      if (!renderedCountryCodes.has(code)) count += 1;
    });
    return count;
  }, [debouncedQuery, matchingCountryCodes, renderedCountryCodes]);
  const skeletonCount = Math.min(pendingCountryCount, COUNTRY_PAGE_SIZE);

  const revealMoreCountries = () => {
    setRenderedCountryCodes((prev) => {
      const additions = countriesForBrowsing
        .filter((country) => !prev.has(country.code))
        .slice(0, COUNTRY_PAGE_SIZE)
        .map((country) => country.code);
      if (!additions.length) return prev;
      const next = new Set(prev);
      additions.forEach((code) => next.add(code));
      return next;
    });
  };

  // Chỉ mount những mã ĐÃ được "lộ" (renderedCountryCodes) — không mount hết 267 nước ngay từ đầu.
  // Duyệt theo `countriesForBrowsing` (16 nước "Phổ biến" lên đầu) để card phổ biến luôn hiện trước.
  const renderedCountries = useMemo(
    () => countriesForBrowsing.filter((country) => renderedCountryCodes.has(country.code)),
    [countriesForBrowsing, renderedCountryCodes],
  );
  const hasMoreCountriesToReveal = renderedCountryCodes.size < countries.length;

  return (
    <div className="esim-hub__tabs">
      <div className="esim-hub__tabs-nav" role="tablist">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={`esim-hub__tabs-btn${tab === item.key ? " esim-hub__tabs-btn--active" : ""}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="esim-hub__tabs-panel">
        {tab === "general" && (
          <>
            <EsimFeaturedCollections />
            <EsimDiscoverByGroup />
            <EsimProductHomeContent />
          </>
        )}

        {tab === "popular" && (
          <Row gutter={[16, 16]}>
            {ESIM_POPULAR_COUNTRY_CODES.map((code) => {
              const country = countryByCode.get(code);
              return (
                <Col xs={12} sm={12} md={6} key={code}>
                  <ErrorBoundary>
                    <EsimDestinationCard
                      countryCode={code}
                      name={country?.countryName ?? code.toUpperCase()}
                      description={getDescription(code)}
                      onClick={getGoToCountryHandler(code)}
                    />
                  </ErrorBoundary>
                </Col>
              );
            })}
          </Row>
        )}

        {tab === "country" && (
          <>
            <Row justify="center" style={{ marginBottom: "3rem" }}>
              <Col xs={24} sm={22} md={18}>
                <Input
                  allowClear
                  size="large"
                  className="esim-hub__tabs-search"
                  prefix={<SearchOutlined style={{ color: "rgba(21, 150, 212, 0.6)", fontSize: "1.6rem" }} />}
                  placeholder={t("esim.search_country_placeholder")}
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                />
                <div className="esim-hub__tabs-search-hint">{t("esim.hub_tab_country_hint")}</div>
              </Col>
            </Row>

            {countriesLoading && !countries.length ? (
              <Row justify="center" style={{ padding: 48 }}>
                <Spin />
              </Row>
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  {renderedCountries.map((country) => (
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      key={country.code}
                      style={{
                        display:
                          !debouncedQuery || matchingCountryCodes.has(country.code) ? undefined : "none",
                      }}
                    >
                      <ErrorBoundary>
                        <EsimDestinationCard
                          countryCode={country.code}
                          name={country.countryName}
                          description={getDescription(country.code)}
                          onClick={getGoToCountryHandler(country.code)}
                        />
                      </ErrorBoundary>
                    </Col>
                  ))}
                  {isRevealingBatch &&
                    Array.from({ length: skeletonCount }).map((_, index) => (
                      <Col xs={12} sm={12} md={6} key={`country-skeleton-${index}`}>
                        <Card className="esim-destination-card">
                          <Skeleton.Image active style={{ width: "100%", height: 200 }} />
                          <Skeleton active title={false} paragraph={{ rows: 2 }} style={{ marginTop: 12 }} />
                        </Card>
                      </Col>
                    ))}
                </Row>
                {!debouncedQuery && hasMoreCountriesToReveal && (
                  <Row justify="center" style={{ marginTop: "1.5rem" }}>
                    <Col>
                      <Button size="large" onClick={revealMoreCountries}>
                        {t("common.see_more")}
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </>
        )}

        {tab === "region" && (
          countriesLoading && !continents.length ? (
            <Row justify="center" style={{ padding: 48 }}>
              <Spin />
            </Row>
          ) : (
            <Row gutter={[16, 16]}>
              {continents.map((continent) => (
                <Col xs={12} sm={12} md={6} key={continent.id}>
                  <ErrorBoundary>
                    <EsimDestinationCard
                      name={continent.continent_name.vi}
                      description={t("esim.hub_tab_region_desc")}
                      onClick={getGoToRegionHandler(continent)}
                    />
                  </ErrorBoundary>
                </Col>
              ))}
            </Row>
          )
        )}
      </div>
    </div>
  );
};

export default EsimHubTabs;
